// src/pages/Kontakt.js

import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  AppBar, 
  Toolbar, 
  Button,
  TextField,
  Grid,
  Alert
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  AccountBalance as AccountBalanceIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

const Kontakt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    firma: '',
    telefon: '',
    nachricht: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paket = params.get('paket') || '';
    const einheiten = params.get('einheiten') || '';
    const monatNetto = params.get('monat_netto') || '';
    const monatBrutto = params.get('monat_brutto') || '';
    const onboarding = params.get('onboarding') || '';
    const neuvermietung = params.get('neuvermietung') || '';

    const prefillText = [
      params.get('leistung') ? `Leistung: ${params.get('leistung')}` : '',
      paket ? `Paket: ${paket}` : '',
      einheiten ? `Einheiten: ${einheiten}` : '',
      monatNetto ? `Monat netto: ${monatNetto} EUR` : '',
      monatBrutto ? `Monat brutto: ${monatBrutto} EUR` : '',
      onboarding ? `Onboarding: ${onboarding}` : '',
      neuvermietung ? `Neuvermietung: ${neuvermietung}` : '',
    ].filter(Boolean).join('\n');

    if (!prefillText) return;

    setFormData((prev) => ({
      ...prev,
      nachricht: prev.nachricht ? prev.nachricht : `Angebotsanfrage:\n${prefillText}`,
    }));
  }, [location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Hier würde normalerweise ein API-Call erfolgen
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#1a365d' }}>
        <Toolbar>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            <AccountBalanceIcon sx={{ color: '#00bcd4' }} /> immo-automation
          </Typography>
          <Button 
            onClick={() => navigate('/')}
            sx={{ color: 'white' }}
          >
            Zurück zur Startseite
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d', textAlign: 'center' }}>
          Kontaktieren Sie uns
        </Typography>
        <Typography variant="h6" sx={{ color: '#64748b', mb: 6, textAlign: 'center' }}>
          Wir sind für Sie da! Schreiben Sie uns oder rufen Sie uns an.
        </Typography>

        <Grid container spacing={4}>
          {/* Kontaktinformationen */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 4, height: '100%', borderRadius: 3, border: '2px solid #e2e8f0' }}>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ color: '#00bcd4', mr: 2, fontSize: 32 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a365d' }}>
                    E-Mail
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  info@immo-automation.de<br />
                  info@immo-automation.de
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ color: '#00bcd4', mr: 2, fontSize: 32 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a365d' }}>
                    Telefon
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  +49 152 22096381<br />
                  Mo-Fr: 17:00 - 20:00 Uhr; alternativ gerne per Mail oder nach Vereinbarung
                </Typography>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ color: '#00bcd4', mr: 2, fontSize: 32 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a365d' }}>
                    Adresse
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  immo-automation GmbH<br />
                  Brechtestraße 64<br />
                  48431 Rheine<br />
                  Deutschland
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Kontaktformular */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '2px solid #e2e8f0' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a365d' }}>
                Senden Sie uns eine Nachricht
              </Typography>

              {submitted && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Vielen Dank! Wir haben Ihre Nachricht erhalten und melden uns in Kürze bei Ihnen.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vorname *"
                      name="vorname"
                      value={formData.vorname}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nachname *"
                      name="nachname"
                      value={formData.nachname}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="E-Mail *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Firma / Eigentuemergesellschaft"
                      name="firma"
                      value={formData.firma}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Ihre Nachricht *"
                      name="nachricht"
                      multiline
                      rows={6}
                      value={formData.nachricht}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        bgcolor: '#1a365d',
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: '#0f2942'
                        }
                      }}
                    >
                      Nachricht senden
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Kontakt;
